import axiosClient from "../axiosClient";

// Backend response structure
interface OrganizationResponse {
  org_id: string;
  org_name: string;
  agency_id: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  country?: string;
  industry?: string;
  description?: string;
  schema_name?: string;
  status?: string | null;
  created_at: string;
}

// Frontend organization type
export interface Organization {
  id: string;
  name: string;
  agencyId: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  country?: string;
  industry?: string;
  description?: string;
  schemaName?: string;
  status: "active" | "inactive";
  createdAt: string;
  // Fee settings (populated client-side when available)
  feeIncluded?: boolean;
  feeDefaultPercent?: number | null;
  billingMode?: string;
  feeValidFrom?: string;
  feeValidTo?: string | null;
  feeDefaultSettingId?: string;
  feeChannelOverrides?: Array<{
    id: string;
    channel: string;
    fee_percent: number | null;
    valid_from: string;
    valid_to?: string | null;
  }>;
}

// Create organization with admin user
export interface CreateOrganizationWithAdminData {
  organization: {
    org_name: string;
    phone?: string;
    website?: string;
    address?: string;
    city?: string;
    country?: string;
    industry?: string;
    description?: string;
    agency_id?: string;
  };
  admin_user: {
    email: string;
    first_name: string;
    last_name: string;
  };
}

// Update organization (org only, no admin)
export interface UpdateOrganizationData {
  org_id: string;
  org_name: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  country?: string;
  industry?: string;
  description?: string;
  agency_id?: string;
}

// Transform backend response to frontend format
const transformOrganization = (org: OrganizationResponse): Organization => {
  const result: Organization = {
    id: org.org_id,
    name: org.org_name,
    agencyId: org.agency_id,
    status: org.status === "inactive" ? "inactive" : "active",
    createdAt: org.created_at,
  };

  if (org.phone !== undefined) result.phone = org.phone;
  if (org.website !== undefined) result.website = org.website;
  if (org.address !== undefined) result.address = org.address;
  if (org.city !== undefined) result.city = org.city;
  if (org.country !== undefined) result.country = org.country;
  if (org.industry !== undefined) result.industry = org.industry;
  if (org.description !== undefined) result.description = org.description;
  if (org.schema_name !== undefined) result.schemaName = org.schema_name;

  return result;
};

export const organizationsService = {
  // Get all organizations (tenant users only)
  getAll: async () => {
    const { data } = await axiosClient.get<{
      success: boolean;
      responseData: { content: OrganizationResponse[] };
    }>("/iam/organizations/");
    return data.responseData.content
      .map(transformOrganization)
      .sort(
        (a, b) =>
          new Date(b.createdAt ?? "").getTime() -
          new Date(a.createdAt ?? "").getTime(),
      );
  },

  // Get single organization by ID
  getById: async (id: string) => {
    const { data } = await axiosClient.get<{
      success: boolean;
      responseData: OrganizationResponse;
    }>(`/iam/organizations/${id}`);
    return transformOrganization(data.responseData);
  },

  // Create organization with admin user (full onboarding)
  createWithAdmin: async (orgData: CreateOrganizationWithAdminData) => {
    const { data } = await axiosClient.post<{
      success: boolean;
      responseData: {
        organization: OrganizationResponse;
        admin_user: unknown;
      };
    }>("/iam/organizations/create-organization", orgData);
    return {
      organization: transformOrganization(data.responseData.organization),
      adminUser: data.responseData.admin_user,
    };
  },

  // Update organization (POST with org_id in body)
  update: async (orgData: UpdateOrganizationData) => {
    const { data } = await axiosClient.post<{
      success: boolean;
      responseData: OrganizationResponse;
    }>("/iam/organizations/", orgData);
    return transformOrganization(data.responseData);
  },

  // Delete organization
  delete: async (id: string) => {
    const { data } = await axiosClient.delete<{
      success: boolean;
      message: string;
      responseData: null;
    }>(`/iam/organizations/${id}`);
    return data;
  },
};
